import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';

// Shared metadata for the three setup stages. The hero flow card and the intro
// bar both read from here so their labels and jump targets can never drift apart.
export const SETUP_STEPS = [
  { index: '01', title: 'Install', icon: 'ri-download-2-line', tag: 'TERMINAL', target: 'step-install' },
  { index: '02', title: 'Configure', icon: 'ri-equalizer-2-line', tag: 'CHART', target: 'step-configure' },
  { index: '03', title: 'Run', icon: 'ri-play-circle-line', tag: 'AUTOTRADING', target: 'step-run' },
];

interface SetupFlowValue {
  active: number;
  setActive: Dispatch<SetStateAction<number>>;
  stepCount: number;
  goToStep: (oneBasedIndex: number) => void;
}

const SetupFlowContext = createContext<SetupFlowValue | null>(null);

// Holds the "current stage" of the setup walkthrough so the hero flow card and
// the intro bar stay in sync, and exposes a shared jump helper for both.
export function SetupFlowProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState(0);
  const stepCount = SETUP_STEPS.length;

  const goToStep = useCallback((oneBasedIndex: number) => {
    setActive(oneBasedIndex);
    const target = SETUP_STEPS[oneBasedIndex - 1]?.target;
    if (!target) return;
    const el = document.getElementById(target);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const value = useMemo(
    () => ({ active, setActive, stepCount, goToStep }),
    [active, stepCount, goToStep],
  );

  return <SetupFlowContext.Provider value={value}>{children}</SetupFlowContext.Provider>;
}

export function useSetupFlow() {
  const ctx = useContext(SetupFlowContext);
  if (!ctx) throw new Error('useSetupFlow must be used within SetupFlowProvider');
  return ctx;
}